import { useState, useEffect } from 'react';
import { Modal } from '../../../components/Shared/Modal';
import { Button } from '../../../components/Shared/Button';
import { Input } from '../../../components/Shared/Input';
import { Clock, AlertTriangle } from 'lucide-react';
import type { TaskBlock, DayPlanning } from '../../../types/planning';

interface MoveTaskDialogProps {
  isOpen: boolean;
  task: TaskBlock | null;
  targetDay: DayPlanning | null;
  targetEmployeeName: string;
  onConfirm: (newStartTime: string, newEndTime: string) => void;
  onCancel: () => void;
}

export function MoveTaskDialog({
  isOpen,
  task,
  targetDay,
  targetEmployeeName,
  onConfirm,
  onCancel,
}: MoveTaskDialogProps) {
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [overlapWarning, setOverlapWarning] = useState<string | null>(null);
  const [timeValidationError, setTimeValidationError] = useState<string | null>(null);

  // Initialize times when dialog opens
  useEffect(() => {
    if (isOpen && task) {
      setStartTime(task.startTime);
      setEndTime(task.endTime);
      setOverlapWarning(null);
      setTimeValidationError(null);
    }
  }, [isOpen, task]);

  // Validate times and check for overlaps
  useEffect(() => {
    if (!startTime || !endTime || !targetDay) {
      setOverlapWarning(null);
      setTimeValidationError(null);
      return;
    }

    // Validate start < end
    if (startTime >= endTime) {
      setTimeValidationError('Startzeit muss vor der Endzeit liegen');
      setOverlapWarning(null);
      return;
    }

    setTimeValidationError(null);

    // Check for overlaps with existing tasks in target day
    const overlappingTask = targetDay.tasks.find((existingTask) => {
      // Skip the task being moved (if it's in the same day)
      if (existingTask.id === task?.id) return false;

      const newStart = startTime;
      const newEnd = endTime;
      const existingStart = existingTask.startTime;
      const existingEnd = existingTask.endTime;

      // Check if times overlap
      // Overlap occurs if: (newStart < existingEnd) AND (newEnd > existingStart)
      return newStart < existingEnd && newEnd > existingStart;
    });

    if (overlappingTask) {
      setOverlapWarning(
        `Überschneidung mit "${overlappingTask.taskName}" (${overlappingTask.startTime} - ${overlappingTask.endTime})`
      );
    } else {
      setOverlapWarning(null);
    }
  }, [startTime, endTime, targetDay, task]);

  const handleConfirm = () => {
    if (timeValidationError || !startTime || !endTime) return;
    onConfirm(startTime, endTime);
  };

  const handleCancel = () => {
    setStartTime('');
    setEndTime('');
    setOverlapWarning(null);
    setTimeValidationError(null);
    onCancel();
  };

  if (!task || !targetDay) return null;

  const hasError = !!timeValidationError;
  const hasWarning = !!overlapWarning;
  const canSave = !hasError && startTime && endTime;

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} title="Aufgabe verschieben">
      <div className="space-y-4">
        {/* Task Info */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <div className="space-y-2">
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Aufgabe:</span>
              <p className="font-medium text-gray-900 dark:text-gray-100">{task.taskName}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Verschieben zu:</span>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {targetEmployeeName} • {new Date(targetDay.date).toLocaleDateString('de-DE', {
                  weekday: 'short',
                  day: '2-digit',
                  month: '2-digit',
                })}
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Ursprüngliche Zeit:</span>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {task.startTime} - {task.endTime}
              </p>
            </div>
          </div>
        </div>

        {/* Time Inputs */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <Clock className="inline w-4 h-4 mr-1" />
              Neue Startzeit
            </label>
            <Input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className={hasError ? 'border-red-500' : ''}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <Clock className="inline w-4 h-4 mr-1" />
              Neue Endzeit
            </label>
            <Input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className={hasError ? 'border-red-500' : ''}
            />
          </div>
        </div>

        {/* Validation Error */}
        {hasError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800 dark:text-red-200">Ungültige Zeitangabe</p>
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">{timeValidationError}</p>
              </div>
            </div>
          </div>
        )}

        {/* Overlap Warning */}
        {hasWarning && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Zeitüberschneidung erkannt</p>
                <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">{overlapWarning}</p>
                <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-2">
                  Das Backend wird diesen Move ablehnen. Bitte passen Sie die Zeiten an.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Duration Info */}
        {startTime && endTime && !hasError && (
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Dauer: {calculateDuration(startTime, endTime)} Minuten
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <Button variant="secondary" onClick={handleCancel} className="flex-1">
            Abbrechen
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!canSave || hasWarning}
            className="flex-1"
          >
            Verschieben
          </Button>
        </div>

        {/* Hint */}
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          Die Aufgabe wird mit der neuen Zeit verschoben.
        </p>
      </div>
    </Modal>
  );
}

function calculateDuration(startTime: string, endTime: string): number {
  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);
  return (endH * 60 + endM) - (startH * 60 + startM);
}

